/**
 * #979 — source name rules (contract §1, provisional): trimmed, nonempty, at
 * most 80 Unicode code points, duplicates matched case-insensitively within
 * the dictionary. No hard-coded names from the mockups.
 */
import type { CardSource } from './types';

export const SOURCE_NAME_MAX_CODE_POINTS = 80;

/** Code points, not UTF-16 units: an emoji counts once, as the contract says. */
export function codePointLength(text: string): number {
  let n = 0;
  for (const _ of text) n += 1;
  return n;
}

export type SourceNameCheck =
  | { ok: true; name: string }
  | { ok: false; reason: 'empty' | 'too_long' };

/** What a create sends: the trimmed name, or why it can't be sent. */
export function checkSourceName(draft: string): SourceNameCheck {
  const name = draft.trim();
  if (name === '') return { ok: false, reason: 'empty' };
  if (codePointLength(name) > SOURCE_NAME_MAX_CODE_POINTS) return { ok: false, reason: 'too_long' };
  return { ok: true, name };
}

/**
 * The duplicate-matching key. NFC first, so the same visible name typed with
 * a composed or decomposed accent is one name.
 */
export function sourceNameKey(name: string): string {
  return name.trim().normalize('NFC').toLowerCase();
}

/** The existing entry a new name would duplicate, if any. */
export function findDuplicate(sources: readonly CardSource[], draft: string): CardSource | undefined {
  const key = sourceNameKey(draft);
  return sources.find((s) => sourceNameKey(s.name) === key);
}

// A fixed locale: the menu order must not change with the machine's language.
const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

/**
 * Menu order for the existing names: alphabetical, ignoring case and accents,
 * with the stable id as a tie-break so equal-looking names never swap places
 * between renders.
 */
export function sortSources(sources: readonly CardSource[]): CardSource[] {
  return [...sources].sort((a, b) => collator.compare(a.name, b.name) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}
